/**
 * Customer-portal Statement page (BE-11).
 *
 * The signed-in customer's own account with the typing centre, in the customer view: invoices against the money the
 * customer actually paid, one combined balance (invoices and wallet) and the aging. No accounting view and no
 * government fee float (TC-internal). PDF / CSV export of the same period. Same layout as the TC client statement's
 * Customer view (CUSTOMER-STATEMENT.md; KAN-236).
 * Route: /portal/statements
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, Card, CardContent, Grid, Divider, CircularProgress,
  useMediaQuery, useTheme,
} from '@mui/material';
import { PictureAsPdf, TableChart } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { DataTable, PeriodSelector } from '../../components/accounting';
import { formatDate } from '../../utils/accounting/formatters';
import { getPeriodDates, toYmd } from '../../utils/accounting/periodUtils';
import { clientLedgerService } from '../../services/accounting/clientLedger.service';
import { serverMessage } from '../../utils/apiConvert';
import { customerColumns, CustomerSummary, AgingRow } from '../reports/ClientStatement';

// The customer view: with payment lineage (the receipts and credit notes under each invoice), remarks not shortened.
const VIEW = { view: 'customer', lineage: true };
// Only invoices have a portal page; receipts and credit notes appear in the lineage as text.
const COLUMNS = customerColumns(0, (id) => `/portal/invoices/${id}`);

const PortalStatement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [period, setPeriod] = useState('THIS_MONTH');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);

  const range = useCallback(() => {
    const { from, to } = getPeriodDates(period);
    return { from: toYmd(from), to: toYmd(to) };
  }, [period]);

  const loadStatement = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatement(await clientLedgerService.getPortalStatement({ ...range(), ...VIEW }));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load your statement'));
      setStatement(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { loadStatement(); }, [loadStatement]);

  const exportFile = async (format) => {
    setExporting(format);
    setError(null);
    try {
      await clientLedgerService.exportPortalStatement(format, { ...range(), ...VIEW });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const aging = statement?.aging;
  const shownPeriod = statement?.period;

  return (
    <Box sx={{ width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {/* Header: title + period + export */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography sx={{ fontSize: isMobile ? '1rem' : '1.25rem', fontWeight: 700, color: 'secondary.main' }}>
            Statement
          </Typography>
          {shownPeriod && (
            <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
              {statement?.organization?.name ? `${statement.organization.name} · ` : ''}
              {shownPeriod.from ? `${formatDate(shownPeriod.from)} – ${formatDate(shownPeriod.to)}` : `Up to ${formatDate(shownPeriod.to)}`}
            </Typography>
          )}
        </Box>
        <PeriodSelector value={period} onChange={setPeriod} />
        <Button
          variant="text"
          size="small"
          startIcon={exporting === 'pdf' ? <CircularProgress size={14} /> : <PictureAsPdf />}
          onClick={() => exportFile('pdf')}
          disabled={Boolean(exporting) || !statement}
          sx={{ textTransform: 'none', fontSize: fs.xs }}
        >
          PDF
        </Button>
        <Button
          variant="text"
          size="small"
          startIcon={exporting === 'csv' ? <CircularProgress size={14} /> : <TableChart />}
          onClick={() => exportFile('csv')}
          disabled={Boolean(exporting) || !statement}
          sx={{ textTransform: 'none', fontSize: fs.xs }}
        >
          CSV
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Transactions */}
      <DataTable
        columns={COLUMNS}
        data={(statement?.lines || []).map((l, i) => ({ ...l, id: `${l.type}-${l.referenceId || l.ref || 'x'}-${i}` }))}
        loading={loading}
        emptyMessage="No transactions in this period."
        pagination={false}
      />

      {!loading && statement?.note && (
        <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: 0.5 }}>{statement.note}</Typography>
      )}
      {/* Aging on the left, the summary on the right; on a phone the summary comes first */}
      {!loading && (statement?.summary || aging) && (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          {aging && (
            <Grid item xs={12} sm={6} sx={{ order: { xs: 2, sm: 1 } }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                    Outstanding by Age
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <AgingRow label="Current (0-30 days)" amount={aging.current} />
                    <AgingRow label="31-60 days" amount={aging.thirtyDays} />
                    <AgingRow label="61-90 days" amount={aging.sixtyDays} />
                    <AgingRow label="90+ days" amount={aging.ninetyPlus} warn />
                    <Divider sx={{ my: 0.5 }} />
                    <AgingRow label="Total" amount={aging.total} bold />
                    <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>As of today, by invoice date</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
          {statement?.summary && (
            <Grid item xs={12} sm={6} sx={{ order: { xs: 1, sm: 2 } }}>
              <CustomerSummary summary={statement.summary} sx={{ mt: 0, mb: 0, maxWidth: 'none', ml: 0, height: '100%' }} />
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default PortalStatement;
